import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Card from "../../components/Card/todaysPrayerHome/HomeCard";
import { getAllRecentItems } from "../../utils/recentExplorers";
import { getStudyPathProgress } from "../../utils/activeStudyPath";
import { STUDY_PATHS } from "../../data/navigation/studyPaths";
import { labelForRoute } from "../../data/navigation/siteNavigation";
import contentCounts from "../../data/generated/contentCounts.json";
import whatsNewItems from "../../data/generated/whatsNew.json";
import "./Home.css";

/* A path step and a page visit are stamped together; see showPathProgress. */
const SAME_VISIT_MS = 1500;

const SECTION_DATA = {
  books: {
    kicker: "Explore",
    title: "Books of the Bible",
    intro:
      "Explore Scripture through grouped book collections, interactive explorers, maps, timelines, and connected study pages across both the Old and New Testament.",
    groups: [
      {
        heading: "Torah",
        subtext: "Foundations, covenant beginnings, wilderness journey, and law.",
        pages: [
          {
            title: "Genesis Explorer",
            path: "/genesis",
            description:
              "Graphs of events, map of events, trace-the-thread modals, and family structure.",
            bullets: [
              "Event graphs",
              "Map of events",
              "Trace the threads",
              "Families of Genesis",
            ],
          },
          {
            title: "Exodus Explorer",
            path: "/exodus",
            description:
              "Walk through the plagues, the exodus journey, and major moments of deliverance.",
            bullets: ["Plagues", "Journey"],
          },
          {
            title: "Leviticus Explorer",
            path: "/leviticus",
            description:
              "Study offerings, feasts, and clean versus unclean laws in a structured visual format.",
            bullets: ["Offerings", "Feasts", "Clean / unclean rules"],
          },
          {
            title: "Numbers Explorer",
            path: "/numbers",
            description:
              "Follow wilderness events, census data, camp layout, and the scouts path.",
            bullets: [
              "Events",
              "Census",
              "Camp layout",
              "Scouts path / event map",
            ],
          },
          {
            title: "Journey Through the Wilderness",
            path: "/exodus-deuteronomy",
            description:
              "Trace the broader wilderness journey from Exodus to Deuteronomy with maps and journey spotlights.",
            bullets: [
              "Events",
              "Map from Exodus to Moab",
              "Journey spotlights",
            ],
          },
        ],
      },
      {
        heading: "Histories",
        subtext:
          "Conquest, judges, kings, kingdom division, and national history.",
        pages: [
          {
            title: "Joshua — Conquest of Canaan",
            path: "/joshua",
            description:
              "Explore the conquest map, land allocation, battle timeline, and Jericho visual journey.",
            bullets: [
              "Conquest map",
              "Walk the conquest",
              "Land allocation map",
              "Jericho demo",
              "Battles timeline",
            ],
          },
          {
            title: "Period of the Judges",
            path: "/judges",
            description:
              "Study judges cycles through timeline tables, maps, and patterns of idol worship.",
            bullets: [
              "Timeline table",
              "Judges map",
              "Idol worship by judge",
            ],
          },
          {
            title: "United to Divided Kingdoms",
            path: "/1samuel-2chron",
            description:
              "View kingdom event graphs, atlas maps, and clickable kings of Judah and Israel. Explore through the books of Samuel, Kings, and Chronicles with a focus on the rise of monarchy, David's reign, and the divided kingdom history.",
            bullets: [
              "Event graphs",
              "Bible atlas map",
              "Judah / Israel king graphs",
              "King detail views",
            ],
          },
          {
            title: "Post Exile and Return",
            path: "/ezra-nehemiah-esther",
            description:
              "Explore the events of the Babylonian exile, the return under Persian rule, and the restoration of Jerusalem and the temple through the books of Ezra, Nehemiah, and Esther with timelines, maps, and event explorers.",
            bullets: [
              "Babylonian Exile",
              "Return under Persian Rule",
              "Temple and City Restoration",
            ],
          },
        ],
      },
      {
        heading: "Wisdoms",
        subtext:
          "Wisdom sayings, poetry, philosophical reflections, and themes of human experience and divine response.",
        pages: [
          {
            title: "Job: A Guided Conversation",
            path: "/job",
            description:
              "Move through Job one speech at a time, following the argument as it develops from human debate to God's response.",
            bullets: ["Job's speeches", "Friends' responses", "God's response"],
          },
          {
            title: "Psalms Explorer",
            path: "/psalms",
            description:
              "Explore representative psalms connected to various themes, with context and connections across Scripture.",
            bullets: ["Themes", "Context", "Connections"],
          },
          {
            title: "Proverbs: Wisdom for Life",
            path: "/proverbs",
            description: "A collection of wise sayings and teachings, offering guidance for living a righteous and meaningful life.",
            bullets: ["Wisdom sayings", "Life guidance", "Righteous living"],
          },
          {
            title: "Ecclesiastes: Meaning Under the Sun",
            path: "/ecclesiastes",
            description: "A philosophical exploration of the meaning of life, the pursuit of wisdom, and the human experience under the sun.",
            bullets: ["Meaning of life", "Pursuit of wisdom", "Human experience"],
          },
          {
            title: "Song of Songs: A Celebration of Love",
            path: "/song-of-songs",
            description:
              "A poetic dialogue celebrating love, desire, and the beauty of human relationships.",
            bullets: ["Love and desire", "Poetry", "Relationships"],
          },
        ],
      },
      {
        heading: "Major Prophets",
        subtext:
          "Explore the historical setting, prophetic messages, and key themes of the Major Prophets.",
        pages: [
          {
            title: "Isaiah",
            path: "/isaiah",
            description:
              "Explore Isaiah's prophetic ministry, historical setting, major themes, and messianic prophecies.",
            bullets: [
              "Historical setting",
              "Major themes",
              "Messianic prophecies",
            ],
          },
          {
            title: "Jeremiah",
            path: "/jeremiah",
            description:
              "Explore Jeremiah's prophetic ministry, historical setting, major themes, and warnings to Judah.",
            bullets: [
              "Historical setting",
              "Major themes",
              "Warnings to Judah",
            ],
          },
          {
            title: "Ezekiel",
            path: "/ezekiel",
            description:
              "Explore Ezekiel's prophetic ministry, historical setting, major themes, and visions during the exile.",
            bullets: [
              "Historical setting",
              "Major themes",
              "Visions & exile",
            ],
          },
          {
            title: "Daniel",
            path: "/daniel",
            description:
              "Explore Daniel's prophetic ministry, historical setting, major themes, and apocalyptic visions during the Babylonian exile.",
            bullets: [
              "Historical setting",
              "Major themes",
              "Apocalyptic visions",
              "Babylonian exile"
            ],
          },
        ],
      },
      {
        heading: "Minor Prophets",
        subtext: "Explore the historical context, prophetic messages, and key themes of the Minor Prophets.",
        pages: [
          {
            title: "Minor Prophets",
            path: "/minor-prophets",
            description: "Explore the historical context, prophetic messages, and key themes of the twelve Minor Prophets, including Hosea, Joel, Amos, Obadiah, Jonah, Micah, Nahum, Habakkuk, Zephaniah, Haggai, Zechariah, and Malachi.",
            bullets: [
              "Hosea",
              "Joel",
              "Amos",
              "Obadiah",
              "Jonah",
              "Micah",
              "Nahum",
              "Habakkuk",
              "Zephaniah",
              "Haggai",
              "Zechariah",
              "Malachi",
            ],
          }
        ]
      },
      {
        heading: "New Testament",
        subtext:
          "Explore the life of Jesus, the early church, and the teachings of the apostles through the Gospels, Acts, Epistles, and Revelation.",
        pages: [
          {
            title: "A Walk Through the Gospels",
            path: "/walk-through-the-gospels",
            description:
              "Explore the life and teachings of Jesus through the four Gospels, including key events, miracles, parables, and teachings.",
            bullets: ["Gospels", "Miracles", "Parables", "Teachings"],
          },
          {
            title: "Church History through Acts",
            path: "/church-history",
            description:
              "Explore the history of the early church as recorded in the Book of Acts, including the spread of the gospel, key figures, and major events.",
            bullets: [
              "Apostolic age",
              "Persecution",
              "Councils and doctrine",
              "Church fathers"
            ],
          },
          {
            title: "Church Letters",
            path: "/church-letters",
            description:
              "Explore the letters of the New Testament, including the letters of Paul and the General Epistles, with historical context, recipients, and key messages.",
            bullets: ["Paul's Letters", "General Epistles"],
          },
          {
            title: "Revelation",
            path: "/revelation",
            description:
              "Explore the final book of the New Testament, Revelation, including seven churches, seals, trumpets, and bowls.",
            bullets: ["Seven churches", "Seals", "Trumpets", "Bowls"],
          }
        ],
      },
    ],
  },

  people: {
    kicker: "Study",
    title: "Explore Biblical Events, People, and Nations",
    intro:
      "Study biblical people through connected profiles, family lines, relationship links, and event tie-ins across Scripture. Explore the biblical events explorer to study key events across Scripture.",
    pages: [
      {
        title: "Enemies of the Bible",
        path: "/enemies-of-the-bible",
        description:
          "Explore the nations, rulers, idols, giants, and spiritual forces that opposed God and His people throughout Scripture through interactive cards, a historical timeline, and a geographic map.",
        bullets: [
          "Nations & empires",
          "Rulers & giants",
          "Idols & spiritual opposition",
          "Timeline and map views of biblical conflicts",
        ],
      },
      {
        title: "Nations & Groups of the Bible",
        path: "/nations-of-the-bible",
        description:
          "Explore the nations and groups of the Bible through interactive cards, a historical timeline, and a geographic map that traces their interactions with God's people across Scripture.",
        bullets: [
          "Nations & groups",
          "Lineage groups",
          "Timeline and map views of biblical nations",
        ],
      },
      {
        title: "Women of the Bible",
        path: "/women-of-the-bible",
        description:
          "Explore the women of Scripture, their family connections, roles, and related biblical events through connected profiles and a historical timeline.",
        bullets: [
          "Connected profiles",
          "Family links",
          "Related events and timeline view",
        ],
      },
      {
        title: "Bible Events Explorer",
        path: "/events",
        description:
          "Explore significant events in the Bible through interactive cards, timelines, and maps.",
        bullets: [
          "Key biblical events",
          "Interactive timelines",
          "Geographic maps",
        ],
      },

    ],
  },

  timelines: {
    kicker: "Explore History",
    title: "History & Text",
    intro:
      "Explore the biblical world through historical timelines and guided context pages that trace major empires, events, and transitions from David to the early church.",

    groups: [
      {
        heading: "Historical Timelines",
        subtext:
          "Explore the historical flow of kingdoms, empires, major events, and transitions that shape the biblical world and early church history.",
        pages: [
          {
            title: "Who Ruled the Biblical Land?",
            path: "/who-ruled-the-biblical-land",
            description: 
              "Explore the rulers of the biblical lands, from the Persian Empire to the Roman Empire, and their impact on the biblical narrative.",
            bullets: [
              "Divided Kingdoms",
              "Babylonian Empire",
              "Persian Empire",
              "Greek Empire",
              "Roman Empire",
            ],
          },
          {
            title: "David to Alexander",
            path: "/david-alexander",
            description:
              "An interactive historical explorer following the biblical world from the reign of David through the divided kingdoms, exile, Persian restoration, and the rise of Alexander the Great.",
            bullets: [
              "Guided story flow",
              "Explore major empires and eras",
              "Timeline and records views",
              "Follow historical progression",
            ],
          },
          {
            title: "Alexander to Early Church",
            path: "/alexander-earlychurch",
            description:
              "An interactive historical explorer tracing the Hellenistic world, Roman expansion, New Testament setting, and the emergence of the early church.",
            bullets: [
              "Explore Hellenistic and Roman history",
              "Bridge into New Testament context",
              "Timeline and records views",
              "Follow the rise of the early church",
            ],
          },
                    {
            title: "Between the Testaments",
            path: "/between-the-testaments",
            description:
              "A guided study page exploring how Persian, Greek, Maccabean, and Roman rule shaped the world between the Old and New Testaments.",
            bullets: [
              "Explore major empires",
              "Study key groups and themes",
              "Follow the story arc",
              "See the world behind the New Testament",
            ],
          },
          {
            title: "Early Church History",
            path: "/early-church-history",
            description:
              "Explore the key events, figures, and developments in the history of the early Christian church, from its beginnings to the early centuries of its growth.",
            bullets: [
              "Apostolic age",
              "Persecution and growth",
              "Councils and doctrinal development",
              "Church fathers and writings",
            ],
          },
          {
            title: "Early Church Fathers",
            path: "/early-church-fathers",
            description:
              "Explore the early church fathers, their writings, influence on Christian doctrine, and role in the development of the early church.",
              bullets: [
                "Writings and teachings",
                "Influence on Christian doctrine",
                "Role in the development of the early church"
              ],
          },
        ],
      },

      {
        heading: "Text & Scripture",
        subtext:
          "Trace how the biblical text was preserved, transmitted, and recognized over time.",
        pages: [
          {
            title: "The Preservation of Scripture",
            path: "/scripture-preservation",
            description:
              "Follow how the biblical text was preserved across time—from ancient scrolls and early manuscripts to printed Bibles and vernacular Scripture.",
            bullets: [
              "Earliest scrolls and Hebrew texts",
              "New Testament papyrus fragments",
              "Major codices and medieval copying",
              "Printing and vernacular Bibles",
            ],
          },
          {
            title: "Scripture Formation & Recognition",
            path: "/scripture-formation-recognition",
            description:
              "Explore the process of canon formation, including early lists, councils, and the reception of Scripture across traditions.",
            bullets: [
              "Early canon lists",
              "Councils and doctrinal clarification",
              "Reception across traditions",
            ],
          },
        ],
      },
    ],
  },

  genealogies: {
    kicker: "Trace",
    title: "Genealogies",
    intro:
      "Trace biblical family lines through interactive genealogy pages with collapsible descendants, connected branches, and color-coded covenant and royal lines.",
    groups: [
      {
        heading: "Early Generations",
        subtext:
          "The earliest generations of Scripture from creation to the flood and the spreading nations after it.",
        pages: [
          {
            title: "Adam to Noah",
            path: "/adam-noah",
            description:
              "Explore the earliest generations of Scripture with collapsible family lines and connected branches.",
            bullets: [
              "Collapsible descendants",
              "Early family line",
              "Color-coded branches",
            ],
          },
          {
            title: "Japheth",
            path: "/japheth-lineage",
            description:
              "Follow the line of Japheth and its connected descendants through an expandable genealogy view.",
            bullets: [
              "Family line explorer",
              "Collapsible branches",
              "Connected descendants",
            ],
          },
          {
            title: "Ham",
            path: "/ham-lineage",
            description:
              "Trace the Hamite line through a structured genealogy layout with expandable descendants.",
            bullets: ["Line of Ham", "Collapsible children", "Branch connections"],
          },
          {
            title: "Shem",
            path: "/shem-lineage",
            description:
              "Explore the line of Shem and the unfolding genealogy leading toward Abraham.",
            bullets: [
              "Line of Shem",
              "Collapsible descendants",
              "Path toward Abraham",
            ],
          },
        ],
      },
      {
        heading: "Patriarchs",
        subtext:
          "The Abrahamic family structure, covenant branches, and the emerging lines of Israel and Edom.",
        pages: [
          {
            title: "Abraham",
            path: "/abraham-lineage",
            description:
              "View Abraham’s family structure, covenant line, and connected household branches.",
            bullets: ["Covenant line", "Family structure", "Connected branches"],
          },
          {
            title: "Haran",
            path: "/haran-lineage",
            description:
              "Follow the Haran branch and its place within the broader Abrahamic family structure.",
            bullets: [
              "Haran branch",
              "Expandable descendants",
              "Family connections",
            ],
          },
          {
            title: "Nahor",
            path: "/nahor-lineage",
            description:
              "Trace Nahor’s line and its related descendants within the broader patriarchal family tree.",
            bullets: [
              "Nahor branch",
              "Collapsible descendants",
              "Patriarchal connections",
            ],
          },
          {
            title: "Jacob",
            path: "/jacob-lineage",
            description:
              "Explore the house of Jacob with tribal lines and connected descendants.",
            bullets: ["House of Jacob", "Tribal structure", "Expandable branches"],
          },
          {
            title: "Esau",
            path: "/esau-lineage",
            description:
              "Follow the family line of Esau and related Edomite branches in a structured genealogy view.",
            bullets: [
              "Line of Esau",
              "Related branches",
              "Collapsible descendants",
            ],
          },
        ],
      },
      {
        heading: "Levitical & Priestly Lines",
        subtext:
          "The household of Levi, priestly descent, and the family structure surrounding Moses and Aaron.",
        pages: [
          {
            title: "Moses & Aaron",
            path: "/moses-aaron",
            description:
              "Trace the family of Moses, Aaron, Miriam, and the priestly branch within the house of Levi.",
            bullets: [
              "Moses and Aaron line",
              "Levitical family structure",
              "Priestly branch connections",
            ],
          },
          {
            title: "Levi Line",
            path: "/levi-line",
            description:
              "Explore the broader line of Levi, including the priestly branch and connected Levitical families.",
            bullets: [
              "Line of Levi",
              "Priestly descendants",
              "Connected Levitical branches",
            ],
          },
          {
            title: "Second Temple Leaders",
            path: "/second-temple-leaders",
            description:
              "Explore the leaders of the Second Temple period, including high priests, prophets, and political figures who shaped Jewish history and set the stage for the New Testament era.",
            bullets: [
              "High priests",
              "Prophets",
              "Political figures",
              "Jewish history",
              "New Testament context",
            ],
          },
        ],
      },
      {
        heading: "Royal Lines",
        subtext:
          "The rise of monarchy through Saul, David, and the royal succession of Judah.",
        pages: [
          {
            title: "Saul",
            path: "/saul-lineage",
            description:
              "Trace the family line associated with Saul through a collapsible royal genealogy layout.",
            bullets: [
              "Royal family line",
              "Collapsible descendants",
              "Connected branches",
            ],
          },
          {
            title: "David",
            path: "/david-lineage",
            description:
              "Explore David’s family line, royal descendants, and key covenant connections.",
            bullets: [
              "Davidic line",
              "Royal descendants",
              "Expandable family structure",
            ],
          },
          {
            title: "Kings of Judah",
            path: "/kings-of-judah",
            description:
              "Follow the royal succession of Judah through a structured genealogy and kingdom line view.",
            bullets: ["Royal succession", "Judah line", "Collapsible king line"],
          },
        ],
      },
    ],
  },
};



const Home = () => {
  const [activeSection, setActiveSection] = useState(null);

  /*
   * Quick filter inside the section modal. "New Testament" and "Royal Lines"
   * are the last group in their modal, so reaching them meant scrolling the
   * whole list.
   */
  const [sectionQuery, setSectionQuery] = useState("");
  const [activeGroup, setActiveGroup] = useState(null);
  const [recentItem, setRecentItem] = useState(null);

  /*
   * A study path in progress, weighed against the last page visited.
   *
   * Read in an effect rather than during render: it comes from localStorage,
   * which the prerendered HTML cannot know about, so reading it while
   * rendering would make the first paint disagree with the markup.
   */
  const [pathProgress, setPathProgress] = useState(null);

  useEffect(() => {
    setPathProgress(getStudyPathProgress());
  }, []);

  /*
   * The study path picker: closed, it is one line of the hero. A grid of all
   * eight cards was tried first and made the hero scroll on a phone before a
   * reader reached anything else.
   */
  const [pathsOpen, setPathsOpen] = useState(false);
  const pathsRef = useRef(null);

  /* Closes on Escape and on a click outside, the way the section modal does. */
  useEffect(() => {
    if (!pathsOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setPathsOpen(false);
    };

    const handlePointerDown = (event) => {
      if (!pathsRef.current?.contains(event.target)) setPathsOpen(false);
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [pathsOpen]);
  const [showRecentPreview, setShowRecentPreview] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const hasSeen = localStorage.getItem("whatsNewSeen");
  const latestUpdateId = whatsNewItems[0]?.id;

  const isNewUpdate = latestUpdateId && latestUpdateId !== hasSeen;
  const [peopleDisplayCount, setPeopleDisplayCount] = useState(0);

  /*
   * Counted at build time. Home is the one page App.js imports eagerly, so
   * reading peopleIndex.length here put all 538 kB of it in the main bundle
   * for every visitor -- to render a number rounded to the nearest hundred.
   */
  const peopleCount = contentCounts.people;
  const roundedPeopleCount = Math.floor(peopleCount / 100) * 100;

  const activeData = useMemo(() => {
    return activeSection ? SECTION_DATA[activeSection] : null;
  }, [activeSection]);

  /*
   * Applies the group chip and the text query to the open section. Groups keep
   * their headings so a filtered result still reads in context.
   */
  const filteredSection = useMemo(() => {
    if (!activeData) return null;

    const query = sectionQuery.trim().toLowerCase();

    const matchesPage = (page) => {
      if (!query) return true;

      return [page.title, page.description, ...(page.bullets || [])]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query));
    };

    if (!activeData.groups) {
      const pages = (activeData.pages || []).filter(matchesPage);
      return { groups: null, pages, total: pages.length };
    }

    const groups = activeData.groups
      .filter((group) => !activeGroup || group.heading === activeGroup)
      .map((group) => ({ ...group, pages: group.pages.filter(matchesPage) }))
      .filter((group) => group.pages.length > 0);

    return {
      groups,
      pages: null,
      total: groups.reduce((sum, group) => sum + group.pages.length, 0),
    };
  }, [activeData, sectionQuery, activeGroup]);

  const openSectionModal = (sectionKey) => {
    setActiveSection(sectionKey);
    setSectionQuery("");
    setActiveGroup(null);
    document.body.style.overflow = "hidden";
  };

  const closeSectionModal = () => {
    setActiveSection(null);
    setSectionQuery("");
    setActiveGroup(null);
    document.body.style.overflow = "";
  };

  useEffect(() => {
    let frame;
    const duration = 900;
    const start = performance.now();

    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      setPeopleDisplayCount(Math.round(eased * roundedPeopleCount));

      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      }
    };

    frame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(frame);
  }, [roundedPeopleCount]);

  useEffect(() => {
    try {
      const items = getAllRecentItems();
      setRecentItem(items.length ? items[0] : null);
    } catch (error) {
      console.error("Home recent item failed:", error);
      setRecentItem(null);
    }
  }, []);

  /*
   * Which of the three "carry on" offers to make: the last person, the last
   * page, or the study path. Whichever happened most recently wins, because
   * that is what the reader was actually doing. Leaving a path -- opening
   * /genesis from the nav rather than the path's own link -- therefore ends
   * the offer to resume it, without any explicit "you have left" bookkeeping.
   *
   * The tolerance covers the one moment the two are written together: every
   * step of a path is also a page visit, so loading /adam-noah?path=... stamps
   * both within a few milliseconds of each other. Without it the page would
   * sometimes edge out the path the reader is standing in the middle of.
   */
  const showPathProgress =
    !!pathProgress &&
    (pathProgress.savedAt || 0) + SAME_VISIT_MS >= (recentItem?.savedAt || 0);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeSectionModal();
    };

    if (activeSection) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [activeSection]);

  const findPageData = (path) => {
    if (!path) return null;

    const allSections = Object.values(SECTION_DATA);

    for (const section of allSections) {
      if (section.groups) {
        for (const group of section.groups) {
          const match = group.pages.find((p) => p.path === path);
          if (match) return match;
        }
      }

      if (section.pages) {
        const match = section.pages.find((p) => p.path === path);
        if (match) return match;
      }
    }

    return null;
  };

  const renderPageCard = (page) => {
    const isDisabled = page.path === "#";

    if (isDisabled) {
      return (
        <div
          key={`${page.title}-${page.description}`}
          className="home-modalCard home-modalCard--disabled"
        >
          <div className="home-modalCardTitle">{page.title}</div>
          <div className="home-modalCardText">{page.description}</div>

          {page.bullets?.length ? (
            <div className="home-modalBullets">
              {page.bullets.map((item) => (
                <span key={item} className="home-modalBullet">
                  {item}
                </span>
              ))}
            </div>
          ) : null}

          <div className="home-modalCardAction">Planned section</div>
        </div>
      );
    }

    return (
      <Link
        key={page.path}
        to={page.path}
        className="home-modalCard"
        onClick={closeSectionModal}
      >
        <div className="home-modalCardTitle">{page.title}</div>
        <div className="home-modalCardText">{page.description}</div>

        {page.bullets?.length ? (
          <div className="home-modalBullets">
            {page.bullets.map((item) => (
              <span key={item} className="home-modalBullet">
                {item}
              </span>
            ))}
          </div>
        ) : null}

        <div className="home-modalCardAction">Open page →</div>
      </Link>
    );
  };

  const getRecentDisplayText = () => {
    if (!recentItem) return "";
    return recentItem.title || recentItem.name || "";
  };

  const getRecentPreviewKicker = () => {
    if (!recentItem) return "";

    return recentItem.type === "person"
      ? "Continue studying this person"
      : "Continue studying";
  };

  const getRecentPreviewTitle = () => {
    if (!recentItem) return "";

    if (recentItem.type === "person") {
      return recentItem.name || "";
    }

    return recentItem.title || recentItem.name || "";
  };

  const getRecentPreviewMeta = () => {
    if (!recentItem) return "";

    if (recentItem.type === "person") {
      return recentItem.title || "";
    }

    return recentItem.section || "";
  };

  const getRecentPreviewText = () => {
    if (!recentItem) return "";

    if (recentItem.type === "person") {
      return recentItem.summary || "";
    }

    const page = findPageData(recentItem.path);
    return page?.description || "";
  };

  return (
    <div className="home" style={{ height: "100%" }}>
      <section className="home-hero">
        <div className="home-content">
          <div className="home-compassGlow" aria-hidden="true">
            <div className="home-compassRing" />
            <div className="home-compassRing home-compassRing--two" />
            <div className="home-compassCore" />
            <div className="home-compassNeedle home-compassNeedle--north" />
            <div className="home-compassNeedle home-compassNeedle--south" />
            <div className="home-compassLetter home-compassLetter--n">N</div>
            <div className="home-compassLetter home-compassLetter--e">E</div>
            <div className="home-compassLetter home-compassLetter--s">S</div>
            <div className="home-compassLetter home-compassLetter--w">W</div>
          </div>

          <div className="bookmark3">
            Bible Study • Timelines • Genealogies • Journey
          </div>

          <h1 className="home-title">Compass Through The Bible</h1>

          <p className="home-subtitle">
            Explore{" "}
            <span className="home-countStat">
              {peopleDisplayCount}
              <span className="home-countPlus">+</span>
            </span>{" "}
            interconnected people across the biblical world—alongside events, timelines,
            maps, genealogies, nations, and the history of Scripture itself.
            Save any person, page or passage, with your own notes, to{" "}
            <Link to="/my-study" className="home-subtitleLink">
              My Study
            </Link>
            .
          </p>

          <div className="home-heroActions">
            <button
              type="button"
              className={`home-whatsNewBtn ${isNewUpdate ? "is-glowing" : ""}`}
              onClick={() => {
                setShowWhatsNew(true);

                if (whatsNewItems[0]) {
                  localStorage.setItem("whatsNewSeen", whatsNewItems[0].id);
                }
              }}
            >
              <span className="home-whatsNewSpark">✦</span>
              <span>What’s New</span>

              {isNewUpdate && <span className="home-whatsNewDot is-glowing" />}

              <span className="home-whatsNewCount">
                {Math.min(whatsNewItems.length, 3)} Updates
              </span>
            </button>

            {showPathProgress || recentItem ? (
              <div className="home-continue">
                <div className="home-continueLabel">
                  <span className="home-continueKicker">Continue Exploring</span>
                </div>

                {showPathProgress ? (
                  <Link
                    to={pathProgress.resumeTo}
                    className="home-continueLink"
                    aria-label={`Continue ${pathProgress.path.title}, step ${pathProgress.step} of ${pathProgress.total}`}
                  >
                    <span className="home-continueMain">
                      <span className="home-continueTitle">
                        {pathProgress.path.title}
                      </span>
                      <span className="home-continueMeta">
                        Step {pathProgress.step} of {pathProgress.total}
                      </span>
                    </span>

                    <span className="home-continueArrow" aria-hidden="true">→</span>
                  </Link>
                ) : recentItem ? (
                  <div
                    className="home-recentPreviewWrap"
                    onMouseEnter={() => setShowRecentPreview(true)}
                    onMouseLeave={() => setShowRecentPreview(false)}
                  >
                    <Link to={recentItem.path} className="home-continueLink">
                      <span className="home-continueMain">
                        <span className="home-continueTitle">
                          {recentItem.type === "person" ? (
                            <>
                              {recentItem.name}
                              {recentItem.title && (
                                <span className="secondary">
                                  {" "}· {recentItem.title}
                                </span>
                              )}
                            </>
                          ) : (
                            getRecentDisplayText()
                          )}
                        </span>
                      </span>

                      <span className="home-continueArrow" aria-hidden="true">→</span>
                    </Link>

                    {showRecentPreview ? (
                      <div className="home-recentPreview home-recentPreview--visible">
                        <div className="home-recentPreviewKicker">
                          {getRecentPreviewKicker()}
                        </div>

                        <div className="home-recentPreviewTitle">
                          {getRecentPreviewTitle()}
                        </div>

                        {getRecentPreviewMeta() ? (
                          <div className="home-recentPreviewMeta">
                            {getRecentPreviewMeta()}
                          </div>
                        ) : null}

                        {getRecentPreviewText() ? (
                          <div className="home-recentPreviewText">
                            {getRecentPreviewText()}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="home-feature-grid">
            <button
              type="button"
              className="home-featureCard home-featureCard--button"
              onClick={() => openSectionModal("books")}
            >
              <div className="home-featureKicker">Explore</div>
              <div className="home-featureTitle">Books of the Bible</div>
              <div className="home-featureText">
                Browse Torah, Histories, Poetry & Wisdom, Prophets, and New Testament explorers.
              </div>
            </button>

            <button
              type="button"
              className="home-featureCard home-featureCard--button"
              onClick={() => openSectionModal("people")}
            >
              <div className="home-featureKicker">Study</div>
              <div className="home-featureTitle">Biblical People & Events</div>
              <div className="home-featureText">
                Study connected profiles, family lines, and relationship links. Explore events to study key events across Scripture.
              </div>
            </button>

            <button
              type="button"
              className="home-featureCard home-featureCard--button"
              onClick={() => openSectionModal("timelines")}
            >
              <div className="home-featureKicker">Visualize</div>
              <div className="home-featureTitle">Historical Timelines</div>
              <div className="home-featureText">
                Explore filterable historical timelines from David to Alexander
                and from Alexander to the early church.
              </div>
            </button>

            <button
              type="button"
              className="home-featureCard home-featureCard--button"
              onClick={() => openSectionModal("genealogies")}
            >
              <div className="home-featureKicker">Trace</div>
              <div className="home-featureTitle">Genealogies</div>
              <div className="home-featureText">
                Explore collapsible family trees, covenant lines, royal
                descendants, and connected generations across Scripture.
              </div>
            </button>
          </div>

                    <section
            className="home-paths"
            ref={pathsRef}
            aria-labelledby="home-study-paths-title"
          >
            <div className="home-pathsHeader">
              <div className="home-pathsCopy">
                <div className="home-pathsKicker">Study Paths</div>
                <h2 id="home-study-paths-title" className="home-pathsTitle">
                  Follow a guided route
                </h2>
                <p className="home-pathsIntro">
                  Pick a story, era, or theme and Compass will keep you on that route.
                </p>
              </div>

              <button
                type="button"
                className="home-pathsToggle"
                aria-expanded={pathsOpen}
                aria-controls="home-paths-panel"
                onClick={() => setPathsOpen((open) => !open)}
              >
                <span>{pathsOpen ? "Close paths" : "View all paths"}</span>
                <span className="home-pathsCaret" aria-hidden="true">
                  {pathsOpen ? "↑" : "→"}
                </span>
              </button>
            </div>

            {/* Desktop/tablet keeps the expandable Study Paths treatment. */}
            <div className="home-pathsDesktop">
              {!pathsOpen ? (
                <div className="home-pathsQuick" aria-label="Featured study paths">
                  {STUDY_PATHS.slice(0, 3).map((studyPath) => (
                    <Link
                      key={studyPath.id}
                      to={`${studyPath.steps[0]}?path=${studyPath.id}`}
                      className="home-pathQuick"
                      style={{
                        "--path-accent": studyPath.accent,
                      }}
                    >
                      <span className="home-pathQuickDot" aria-hidden="true" />
                      <span className="home-pathQuickText">
                        <span className="home-pathQuickTitle">
                          {studyPath.title}
                        </span>
                        <span className="home-pathQuickMeta">
                          {studyPath.steps.length}{" "}
                          {studyPath.steps.length === 1 ? "step" : "steps"}
                        </span>
                      </span>
                      <span className="home-pathQuickArrow" aria-hidden="true">
                        →
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="home-pathsPanel" id="home-paths-panel">
                  <ul className="home-pathsList">
                    {STUDY_PATHS.map((studyPath) => (
                      <li key={studyPath.id}>
                        <Link
                          to={`${studyPath.steps[0]}?path=${studyPath.id}`}
                          className="home-pathsItem"
                          style={{
                            "--path-accent": studyPath.accent,
                          }}
                          onClick={() => setPathsOpen(false)}
                        >
                          <span className="home-pathsItemTop">
                            <span className="home-pathsItemTitle">
                              {studyPath.title}
                            </span>
                            <span className="home-pathsItemCount">
                              {studyPath.steps.length}{" "}
                              {studyPath.steps.length === 1 ? "step" : "steps"}
                            </span>
                          </span>

                          <span className="home-pathsItemBlurb">
                            {studyPath.blurb}
                          </span>

                          <span className="home-pathsItemSteps">
                            {studyPath.steps
                              .map((route) => labelForRoute(route) || route)
                              .join(" → ")}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/*
              Phone layout: every Study Path lives in one horizontal rail.
              There is intentionally no View All expansion on mobile, which
              keeps the hero compact and removes the overflow-prone accordion.
            */}
            <div
              className="home-pathsMobileRail"
              aria-label="Study paths. Swipe left or right to browse."
            >
              {STUDY_PATHS.map((studyPath) => (
                <Link
                  key={studyPath.id}
                  to={`${studyPath.steps[0]}?path=${studyPath.id}`}
                  className="home-pathQuick home-pathQuick--mobile"
                  style={{
                    "--path-accent": studyPath.accent,
                  }}
                >
                  <span className="home-pathQuickDot" aria-hidden="true" />
                  <span className="home-pathQuickText">
                    <span className="home-pathQuickTitle">
                      {studyPath.title}
                    </span>
                    <span className="home-pathQuickMeta">
                      {studyPath.steps.length}{" "}
                      {studyPath.steps.length === 1 ? "step" : "steps"}
                    </span>
                  </span>
                  <span className="home-pathQuickArrow" aria-hidden="true">
                    →
                  </span>
                </Link>
              ))}
            </div>
          </section>

        </div>

        <div className="home-card-wrap">
          <Card />
        </div>
      </section>

      {showWhatsNew ? (
        <div
          className="home-modalOverlay"
          onClick={() => setShowWhatsNew(false)}
          role="presentation"
        >
          <div
            className="home-whatsNewModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-whats-new-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="home-modalHeader">
              <div>
                <div className="home-modalKicker">Latest Updates</div>
                <h2 id="home-whats-new-title" className="home-modalTitle">
                  What’s New
                </h2>
                <p className="home-modalIntro">
                  Recent improvements added to Compass Through The Bible.
                </p>
              </div>

              <button
                type="button"
                className="home-modalClose"
                onClick={() => setShowWhatsNew(false)}
                aria-label="Close what’s new modal"
              >
                ×
              </button>
            </div>

            <div className="home-modalBody">
            <div className="home-whatsNewList">
              {whatsNewItems.slice(0, 3).map((item, index) => (
                <details
                  key={`${item.date}-${item.title}`}
                  className="home-whatsNewItem"
                >
                  <summary className="home-whatsNewSummary">
                    <div className="home-whatsNewItemTop">
                      <span className="home-whatsNewType">{item.type}</span>
                      <span className="home-whatsNewDate">{item.date}</span>

                      {index === 0 && (
                        <span className="home-whatsNewBadge">New</span>
                      )}
                    </div>

                    <h3 style={{ marginTop: "15px" }}>{item.title}</h3>

                    {item.details?.length > 0 && (
                      <span className="home-whatsNewExpandText" />
                    )}
                  </summary>

                  {item.details?.length > 0 && (
                    <ul className="home-whatsNewDetails">
                      {item.details.map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                  )}
                </details>
              ))}
            </div>
            </div>

            <div className="home-modalFooter">
              <button
                type="button"
                className="ctb-modalBtn"
                onClick={() => setShowWhatsNew(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {activeData ? (
        <div
          className="home-modalOverlay"
          onClick={closeSectionModal}
          role="presentation"
        >
          <div
            className="home-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-section-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="home-modalHeader">
              <div>
                <div className="home-modalKicker">{activeData.kicker}</div>
                <h2 id="home-section-modal-title" className="home-modalTitle">
                  {activeData.title}
                </h2>
                <p className="home-modalIntro">{activeData.intro}</p>
              </div>

              <button
                type="button"
                className="home-modalClose"
                onClick={closeSectionModal}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>

            {(activeData.groups?.length || 0) > 1 ||
            (activeData.pages?.length || 0) > 6 ? (
              <div className="home-modalFilter">
                <div className="home-modalSearchWrap">
                  {sectionQuery ? (
                    <button
                      type="button"
                      className="home-modalSearchClear"
                      onClick={() => setSectionQuery("")}
                      aria-label="Clear search"
                    >
                      ×
                    </button>
                  ) : null}
                </div>

                {activeData.groups ? (
                  <div className="home-modalChips">
                    <button
                      type="button"
                      className={`home-modalChip${
                        activeGroup ? "" : " is-active"
                      }`}
                      onClick={() => setActiveGroup(null)}
                      aria-pressed={!activeGroup}
                    >
                      All
                    </button>

                    {activeData.groups.map((group) => (
                      <button
                        key={group.heading}
                        type="button"
                        className={`home-modalChip${
                          activeGroup === group.heading ? " is-active" : ""
                        }`}
                        aria-pressed={activeGroup === group.heading}
                        onClick={() =>
                          setActiveGroup(
                            activeGroup === group.heading ? null : group.heading
                          )
                        }
                      >
                        {group.heading}
                      </button>
                    ))}

                  <input
                    type="search"
                    className="home-modalSearch"
                    placeholder={`Search ${activeData.title.toLowerCase()}...`}
                    value={sectionQuery}
                    onChange={(event) => setSectionQuery(event.target.value)}
                    aria-label={`Search ${activeData.title}`}
                  />
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="home-modalBody">
            {filteredSection && filteredSection.total === 0 ? (
              <p className="home-modalNoResults">
                Nothing matches “{sectionQuery}”
                {activeGroup ? ` in ${activeGroup}` : ""}.
              </p>
            ) : filteredSection?.groups ? (
              <div className="home-modalSections">
                {filteredSection.groups.map((group) => (
                  <section key={group.heading} className="home-modalSection">
                    <div className="home-modalSectionHead">
                      <h3 className="home-modalSectionTitle">{group.heading}</h3>
                      {group.subtext ? (
                        <p className="home-modalSectionText">
                          {group.subtext}
                        </p>
                      ) : null}
                    </div>

                    <div className="home-modalGrid">
                      {group.pages.map((page) => renderPageCard(page))}
                    </div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="home-modalGrid">
                {(filteredSection?.pages || []).map((page) =>
                  renderPageCard(page)
                )}
              </div>
            )}
            </div>

            <div className="home-modalFooter">
              <button
                type="button"
                className="ctb-modalBtn"
                onClick={closeSectionModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Home;